import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '~/entities/common/viewer'
import { CarLicensee, CarLicenseeEmployee } from '~/shared/api'
import { CarLicenseeEmployeeTypeEnum } from '~/shared/config/enums'
import { AsyncGrid } from '~/shared/ui/AsyncGrid'
import type { FetchRowsFn } from '~/shared/ui/AsyncGrid/types'
import { Button } from '~/shared/ui/Buttons'
import { PlusIcon } from '~/shared/ui/Icons'
import { columns } from './columns'
import { filters } from './filters'
import { GridRow } from './types'

const getEmployees = (
  employees: CarLicenseeEmployee[] | undefined,
  type: CarLicenseeEmployeeTypeEnum,
) =>
  (employees || [])
    ?.filter((employee) => employee.getEmployeeType() === type)
    .map((employees) => employees.getFullName())

export const ListCarLicensees = () => {
  const { t } = useTranslation()

  const { canUpdateCarLicensees } = usePermissions()

  const fetchRows = useCallback<FetchRowsFn<GridRow>>(
    async (page, pageSize, filters = []) => {
      let builder = CarLicensee.limit(pageSize)
        .with('carLicenseeEmployees')
        .orderBy('-createdAt')

      filters.forEach(({ key, value }) => {
        builder = builder.where(key, value)
      })

      const response = await builder.get(page)

      const data = response.getData()
      const rows = data.map((carLicensee: CarLicensee) => {
        const employees = carLicensee?.getCarLicenseeEmployees()
        return {
          ...carLicensee.getAttributes(),
          id: carLicensee.getApiId() as string,
          dispatchers: getEmployees(
            employees,
            CarLicenseeEmployeeTypeEnum.dispatcher,
          ),
          controllers: getEmployees(
            employees,
            CarLicenseeEmployeeTypeEnum.controller,
          ),
          doctors: getEmployees(employees, CarLicenseeEmployeeTypeEnum.doctor),
        }
      })

      /*
       * Coloquent не дает метода, чтобы получить объект meta,
       * поэтому в данном случае получаем оригинальный ответ и достаем мету из него
       * TODO: Реализовать лучший способ получения метаданных
       *  ...
       * */
      const httpClientResponse = response.getHttpClientResponse()
      const axiosResponse = httpClientResponse?.getUnderlying()
      const total = axiosResponse?.data?.meta?.page?.total || 0

      return {
        rows,
        total,
      }
    },
    [],
  )

  return (
    <AsyncGrid<GridRow>
      smallTitle={t('licensees')}
      gridKey='carLicensees'
      fetchRows={fetchRows}
      columns={columns}
      filters={filters}
      headerExtra={
        <Button
          startDecorator={<PlusIcon />}
          to='new'
          show={canUpdateCarLicensees}
        >
          {t('button.add')}
        </Button>
      }
    />
  )
}
